/**
 * OnboardingFunnelCard — Admin-only chart of new-signup activation.
 * Shows per-step completion % + median signup→step minutes, plus the
 * "fully activated" rate across the entire 5-step checklist.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

const STEP_LABEL = {
  add_lead: "Add first lead",
  run_analyzer: "Analyze a deal",
  set_brand: "Set brand",
  add_team_member: "Add buyer",
  share_report: "Share a deal",
};

function fmtMin(min) {
  if (!min || min === 0) return "—";
  if (min < 60) return `${min.toFixed(0)} min`;
  const hrs = min / 60;
  if (hrs < 24) return `${hrs.toFixed(1)} hr`;
  return `${(hrs / 24).toFixed(1)} d`;
}

export default function OnboardingFunnelCard({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/onboarding-funnel", { params: { days } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load onboarding funnel");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { refresh(); }, [refresh]);

  const sendNudgeTest = async () => {
    setTesting(true);
    try {
      await api.post("/me/onboarding/activation-nudge/test");
      toast.success("Activation nudge email sent to your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-6 text-center text-[var(--ink-3)]">
        <Loader2 size={16} className="inline animate-spin" /> Loading activation funnel…
      </div>
    );
  }
  if (!data) return null;

  const maxCount = Math.max(1, ...data.steps.map((s) => s.users_completed));

  return (
    <div className="card p-4" data-testid="admin-onboarding-funnel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs text-[var(--ink-2)] uppercase tracking-wide">
          <Sparkles size={12} /> Onboarding activation · last {data.window_days}d cohort
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={sendNudgeTest}
            disabled={testing}
            className="btn-outline text-xs"
            data-testid="activation-nudge-test-btn"
          >
            {testing ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Preview stuck-user email
          </button>
          <div className="text-[10px] text-[var(--ink-3)]">
            {data.cohort_signups} new signups
          </div>
        </div>
      </div>

      {/* Headline activation rate */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Fully activated" value={data.fully_activated} />
        <Stat label="Activation rate" value={`${data.activation_pct}%`} accent />
        <Stat label="Cohort size" value={data.cohort_signups} />
      </div>

      {/* Per-step bars */}
      <div className="space-y-2.5">
        {data.steps.map((s, i) => {
          const widthPct = (s.users_completed / maxCount) * 100;
          return (
            <div key={s.step_key} data-testid={`onboarding-funnel-step-${s.step_key}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-semibold flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                  {STEP_LABEL[s.step_key] || s.step_key}
                </span>
                <span className="tabular-nums text-[var(--ink-2)]">
                  <strong>{s.users_completed}</strong>
                  <span className="text-[var(--ink-3)]"> · {s.pct_of_signups}%</span>
                  <span className="ml-3 text-[var(--ink-3)]">
                    median: <strong className="text-[var(--ink-2)]">{fmtMin(s.median_minutes)}</strong>
                  </span>
                </span>
              </div>
              <div className="h-2 bg-[var(--bg-2)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500"
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[10px] text-[var(--ink-3)] mt-3 leading-relaxed">
        &ldquo;Median&rdquo; = the typical minutes between signup and that step being completed for users who finished it.
        Steps below 20% in a healthy onboarding usually mean the CTA flow needs work.
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={"rounded-lg border px-3 py-2.5 " + (accent ? "bg-emerald-50 border-emerald-200" : "bg-[var(--bg-2)] border-[var(--line)]")}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">{label}</div>
      <div className={"font-display font-extrabold text-lg tabular-nums mt-0.5 " + (accent ? "text-emerald-700" : "text-[var(--ink)]")}>
        {value}
      </div>
    </div>
  );
}
